import supabase from '../lib/supabase';

class SupabaseService {
  constructor() {
    this.initializeTables();
  }

  async initializeTables() {
    try {
      // Check if we can query categories to see if tables exist
      const { data, error } = await supabase
        .from('categories_portal2025')
        .select('*')
        .limit(1);

      if (error && error.code === '42P01') {
        // Table doesn't exist, but we can't create it via client
        console.log('Database tables need to be created via Supabase dashboard');
        // Use mock data for now
        this.usesMockData = true;
      } else {
        // Tables exist, insert sample data if needed
        await this.ensureSampleData();
      }
    } catch (error) {
      console.error('Error checking tables:', error);
      this.usesMockData = true;
    }
  }

  async ensureSampleData() {
    try {
      // Check if categories exist
      const { data: categories } = await supabase
        .from('categories_portal2025')
        .select('*')
        .limit(1);

      if (!categories || categories.length === 0) {
        await this.insertSampleData();
      }
    } catch (error) {
      console.error('Error ensuring sample data:', error);
    }
  }

  async insertSampleData() {
    try {
      // Insert categories
      const { error: catError } = await supabase
        .from('categories_portal2025')
        .upsert([
          { name: 'Sales', color: '#3b82f6' },
          { name: 'Finance', color: '#10b981' },
          { name: 'Operations', color: '#f59e0b' },
          { name: 'Marketing', color: '#8b5cf6' },
          { name: 'HR', color: '#ef4444' }
        ], { onConflict: 'name' });

      if (catError) console.error('Error inserting categories:', catError);

      // Insert companies
      const { error: compError } = await supabase
        .from('companies_portal2025')
        .upsert([
          {
            name: 'Brioche Bakery',
            description: 'Premium artisanal bakery chain specializing in fresh brioche and pastries',
            logo: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=200&h=200&fit=crop&crop=center'
          },
          {
            name: 'Enjoy Shops',
            description: 'Modern retail experience with curated lifestyle products',
            logo: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=200&h=200&fit=crop&crop=center'
          },
          {
            name: 'TechFlow Solutions',
            description: 'Digital transformation and software development services',
            logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200&h=200&fit=crop&crop=center'
          }
        ], { onConflict: 'name' });

      if (compError) console.error('Error inserting companies:', compError);

      // Get companies and categories for sample assets
      const { data: companiesData } = await supabase
        .from('companies_portal2025')
        .select('*');

      const { data: categoriesData } = await supabase
        .from('categories_portal2025')
        .select('*');

      if (companiesData && companiesData.length > 0 && categoriesData && categoriesData.length > 0) {
        // Insert sample assets for each company
        const sampleAssets = [];
        
        companiesData.forEach((company, companyIndex) => {
          const assetsForCompany = [
            {
              title: `${company.name} Sales Dashboard`,
              description: 'Real-time sales performance and analytics',
              url: `https://example.com/${company.name.toLowerCase().replace(/\s+/g, '-')}/sales`,
              company_id: company.id,
              category_id: categoriesData[0]?.id, // Sales
              provider: 'Tableau',
              tags: ['sales', 'real-time', 'kpi']
            },
            {
              title: `${company.name} Financial Reports`,
              description: 'Monthly and quarterly financial analysis',
              url: `https://example.com/${company.name.toLowerCase().replace(/\s+/g, '-')}/finance`,
              company_id: company.id,
              category_id: categoriesData[1]?.id, // Finance
              provider: 'Power BI',
              tags: ['finance', 'monthly', 'quarterly']
            },
            {
              title: `${company.name} Operations Overview`,
              description: 'Operational metrics and performance indicators',
              url: `https://example.com/${company.name.toLowerCase().replace(/\s+/g, '-')}/operations`,
              company_id: company.id,
              category_id: categoriesData[2]?.id, // Operations
              provider: 'Looker',
              tags: ['operations', 'metrics', 'performance']
            }
          ];
          
          sampleAssets.push(...assetsForCompany);
        });

        const { error: assetsError } = await supabase
          .from('assets_portal2025')
          .upsert(sampleAssets, { onConflict: 'title' });

        if (assetsError) console.error('Error inserting sample assets:', assetsError);
      }

    } catch (error) {
      console.error('Error inserting sample data:', error);
    }
  }

  // Authentication methods
  async signUp(email, password, userData = {}) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  async signIn(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  // Companies methods
  async getCompanies(userId = null) {
    try {
      if (this.usesMockData) {
        return this.getMockCompanies();
      }

      let query = supabase
        .from('companies_portal2025')
        .select('*')
        .order('created_at', { ascending: false });

      // If userId provided, filter by user access
      if (userId) {
        const { data: userCompanies } = await supabase
          .from('user_companies_portal2025')
          .select('company_id')
          .eq('user_id', userId);

        if (userCompanies && userCompanies.length > 0) {
          const companyIds = userCompanies.map(uc => uc.company_id);
          query = query.in('id', companyIds);
        } else {
          // If no specific company access, return all companies for demo
          // In production, you'd return empty array for non-admin users
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      // Add assets count for each company
      const companiesWithCounts = await Promise.all(
        (data || []).map(async (company) => {
          const { count } = await supabase
            .from('assets_portal2025')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', company.id);

          return {
            ...company,
            assetsCount: count || 0,
            lastUpdated: company.updated_at || company.created_at
          };
        })
      );

      return companiesWithCounts;
    } catch (error) {
      console.error('Get companies error:', error);
      // Return mock data if database isn't ready
      return this.getMockCompanies();
    }
  }

  getMockCompanies() {
    return [
      {
        id: 'mock-1',
        name: 'Brioche Bakery',
        description: 'Premium artisanal bakery chain specializing in fresh brioche and pastries',
        logo: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=200&h=200&fit=crop&crop=center',
        assetsCount: 8,
        lastUpdated: new Date().toISOString()
      },
      {
        id: 'mock-2',
        name: 'Enjoy Shops',
        description: 'Modern retail experience with curated lifestyle products',
        logo: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=200&h=200&fit=crop&crop=center',
        assetsCount: 12,
        lastUpdated: new Date().toISOString()
      },
      {
        id: 'mock-3',
        name: 'TechFlow Solutions',
        description: 'Digital transformation and software development services',
        logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200&h=200&fit=crop&crop=center',
        assetsCount: 15,
        lastUpdated: new Date().toISOString()
      }
    ];
  }

  async createCompany(companyData) {
    try {
      if (this.usesMockData) {
        // Simulate creation for demo
        return {
          id: `mock-${Date.now()}`,
          ...companyData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }

      const { data, error } = await supabase
        .from('companies_portal2025')
        .insert([{
          name: companyData.name,
          description: companyData.description,
          logo: companyData.logo,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create company error:', error);
      throw error;
    }
  }

  async updateCompany(id, updates) {
    try {
      if (this.usesMockData) {
        return { id, ...updates, updated_at: new Date().toISOString() };
      }

      const { data, error } = await supabase
        .from('companies_portal2025')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update company error:', error);
      throw error;
    }
  }

  async deleteCompany(id) {
    try {
      if (this.usesMockData) {
        return true;
      }

      const { error } = await supabase
        .from('companies_portal2025')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Delete company error:', error);
      throw error;
    }
  }

  // Assets methods
  async getAssets(companyId, filters = {}) {
    try {
      if (this.usesMockData) {
        return this.getMockAssets(companyId);
      }

      let query = supabase
        .from('assets_portal2025')
        .select(`
          *,
          categories_portal2025(*)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      // Apply category filter
      if (filters.category) {
        query = query.eq('category_id', filters.category);
      }

      // Apply search filter
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Transform data to match existing interface
      return (data || []).map(asset => ({
        ...asset,
        categoryId: asset.category_id,
        companyId: asset.company_id,
        lastChecked: asset.updated_at,
        tags: asset.tags || []
      }));
    } catch (error) {
      console.error('Get assets error:', error);
      return this.getMockAssets(companyId);
    }
  }

  getMockAssets(companyId) {
    const mockAssets = [
      {
        id: 'asset-1',
        title: 'Sales Dashboard',
        description: 'Real-time sales performance and analytics',
        url: 'https://example.com/sales-dashboard',
        categoryId: 'cat-1',
        companyId: companyId,
        provider: 'Tableau',
        tags: ['sales', 'real-time', 'kpi'],
        status: 'active',
        lastChecked: new Date().toISOString()
      },
      {
        id: 'asset-2',
        title: 'Financial Reports',
        description: 'Monthly and quarterly financial analysis',
        url: 'https://example.com/financial-reports',
        categoryId: 'cat-2',
        companyId: companyId,
        provider: 'Power BI',
        tags: ['finance', 'monthly', 'quarterly'],
        status: 'active',
        lastChecked: new Date().toISOString()
      },
      {
        id: 'asset-3',
        title: 'Operations Overview',
        description: 'Operational metrics and performance indicators',
        url: 'https://example.com/operations',
        categoryId: 'cat-3',
        companyId: companyId,
        provider: 'Looker',
        tags: ['operations', 'metrics'],
        status: 'active',
        lastChecked: new Date().toISOString()
      },
      {
        id: 'asset-4',
        title: 'Marketing Analytics',
        description: 'Campaign performance and customer insights',
        url: 'https://example.com/marketing',
        categoryId: 'cat-4',
        companyId: companyId,
        provider: 'Google Analytics',
        tags: ['marketing', 'campaigns'],
        status: 'active',
        lastChecked: new Date().toISOString()
      },
      {
        id: 'asset-5',
        title: 'HR Metrics',
        description: 'Employee performance and engagement data',
        url: 'https://example.com/hr',
        categoryId: 'cat-5',
        companyId: companyId,
        provider: 'Workday',
        tags: ['hr', 'employees'],
        status: 'active',
        lastChecked: new Date().toISOString()
      }
    ];

    return mockAssets;
  }

  async createAsset(assetData) {
    try {
      if (this.usesMockData) {
        return {
          id: `asset-${Date.now()}`,
          ...assetData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }

      const { data, error } = await supabase
        .from('assets_portal2025')
        .insert([{
          title: assetData.title,
          description: assetData.description,
          url: assetData.url,
          company_id: assetData.companyId,
          category_id: assetData.categoryId,
          provider: assetData.provider,
          tags: assetData.tags || [],
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Create asset error:', error);
      throw error;
    }
  }

  async updateAsset(id, updates) {
    try {
      if (this.usesMockData) {
        return { id, ...updates, updated_at: new Date().toISOString() };
      }

      const { data, error } = await supabase
        .from('assets_portal2025')
        .update({
          title: updates.title,
          description: updates.description,
          url: updates.url,
          company_id: updates.companyId,
          category_id: updates.categoryId,
          provider: updates.provider,
          tags: updates.tags || [],
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update asset error:', error);
      throw error;
    }
  }

  async deleteAsset(id) {
    try {
      if (this.usesMockData) {
        return true;
      }

      const { error } = await supabase
        .from('assets_portal2025')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Delete asset error:', error);
      throw error;
    }
  }

  // Categories methods
  async getCategories() {
    try {
      if (this.usesMockData) {
        return this.getMockCategories();
      }

      const { data, error } = await supabase
        .from('categories_portal2025')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || this.getMockCategories();
    } catch (error) {
      console.error('Get categories error:', error);
      return this.getMockCategories();
    }
  }

  getMockCategories() {
    return [
      { id: 'cat-1', name: 'Sales', color: '#3b82f6' },
      { id: 'cat-2', name: 'Finance', color: '#10b981' },
      { id: 'cat-3', name: 'Operations', color: '#f59e0b' },
      { id: 'cat-4', name: 'Marketing', color: '#8b5cf6' },
      { id: 'cat-5', name: 'HR', color: '#ef4444' }
    ];
  }

  // Audit logs
  async logAssetClick(assetId, companyId, userId) {
    try {
      if (this.usesMockData) {
        return; // Skip logging in mock mode
      }

      const { data, error } = await supabase
        .from('audit_logs_portal2025')
        .insert([{
          user_id: userId,
          asset_id: assetId,
          company_id: companyId,
          action: 'click',
          timestamp: new Date().toISOString(),
          ip_address: 'unknown',
          user_agent: navigator.userAgent
        }]);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Log asset click error:', error);
      // Don't throw error for logging failures
    }
  }

  async getAuditLogs(filters = {}) {
    try {
      if (this.usesMockData) {
        return this.getMockAuditLogs();
      }

      let query = supabase
        .from('audit_logs_portal2025')
        .select(`
          *,
          assets_portal2025(title),
          companies_portal2025(name)
        `)
        .order('timestamp', { ascending: false })
        .limit(100);

      const { data, error } = await query;
      if (error) throw error;

      // Transform data to match existing interface
      return (data || []).map(log => ({
        ...log,
        userName: log.user_email || 'Unknown User',
        assetTitle: log.assets_portal2025?.title || 'Unknown Asset',
        companyName: log.companies_portal2025?.name || 'Unknown Company',
        ip: log.ip_address
      }));
    } catch (error) {
      console.error('Get audit logs error:', error);
      return this.getMockAuditLogs();
    }
  }

  getMockAuditLogs() {
    return [
      {
        id: '1',
        userId: 'user-1',
        userName: 'Admin User',
        assetId: 'asset-1',
        assetTitle: 'Sales Dashboard',
        companyId: 'company-1',
        companyName: 'Brioche Bakery',
        action: 'click',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        ip: '192.168.1.100'
      },
      {
        id: '2',
        userId: 'user-2',
        userName: 'Executive User',
        assetId: 'asset-2',
        assetTitle: 'Financial Reports',
        companyId: 'company-2',
        companyName: 'Enjoy Shops',
        action: 'click',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        ip: '192.168.1.101'
      }
    ];
  }

  // User management methods
  async getAllUsers() {
    try {
      if (this.usesMockData) {
        return this.getMockUsers();
      }

      // First get all user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles_portal2025')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching user profiles:', profilesError);
        return this.getMockUsers();
      }

      // Then get auth users (admin only operation)
      try {
        const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
        
        if (authError) {
          console.warn('Could not fetch auth users (admin required):', authError);
          // Return profiles only if we can't access auth users
          return profiles || [];
        }

        // Merge auth data with profiles
        const mergedUsers = (authUsers.users || []).map(authUser => {
          const profile = profiles?.find(p => p.user_id === authUser.id);
          return {
            id: profile?.id || authUser.id,
            user_id: authUser.id,
            name: profile?.name || authUser.user_metadata?.name || authUser.email.split('@')[0],
            email: authUser.email,
            roles: profile?.roles || ['Viewer'],
            created_at: profile?.created_at || authUser.created_at,
            last_sign_in_at: authUser.last_sign_in_at
          };
        });

        return mergedUsers;
      } catch (adminError) {
        console.warn('Admin operations not available:', adminError);
        return profiles || [];
      }
    } catch (error) {
      console.error('Get all users error:', error);
      return this.getMockUsers();
    }
  }

  getMockUsers() {
    return [
      {
        id: '1',
        user_id: 'auth-user-1',
        name: 'Admin User',
        email: 'admin@company.com',
        roles: ['Admin', 'Executive'],
        created_at: '2024-01-10T10:30:00Z',
        last_sign_in_at: '2024-01-16T10:30:00Z',
        companyAccess: ['mock-1', 'mock-2', 'mock-3'],
        assetPermissions: {
          'asset-1': true,
          'asset-2': true,
          'asset-3': true,
          'asset-4': true,
          'asset-5': true
        }
      },
      {
        id: '2',
        user_id: 'auth-user-2',
        name: 'Executive User',
        email: 'exec@company.com',
        roles: ['Executive'],
        created_at: '2024-01-12T14:20:00Z',
        last_sign_in_at: '2024-01-15T14:20:00Z',
        companyAccess: ['mock-1', 'mock-2'],
        assetPermissions: {
          'asset-1': true,
          'asset-2': true,
          'asset-3': false,
          'asset-4': true,
          'asset-5': false
        }
      },
      {
        id: '3',
        user_id: 'auth-user-3',
        name: 'Carlo Gunter',
        email: 'carlo.gunter@gmail.com',
        roles: ['Admin'],
        created_at: '2024-01-16T12:00:00Z',
        last_sign_in_at: '2024-01-16T15:30:00Z',
        companyAccess: ['mock-1', 'mock-2', 'mock-3'],
        assetPermissions: {
          'asset-1': true,
          'asset-2': true,
          'asset-3': true,
          'asset-4': true,
          'asset-5': true
        }
      }
    ];
  }

  async createUserProfile(userId, userData) {
    try {
      if (this.usesMockData) {
        return {
          id: `profile-${Date.now()}`,
          user_id: userId,
          ...userData,
          created_at: new Date().toISOString()
        };
      }

      const { data, error } = await supabase
        .from('user_profiles_portal2025')
        .insert([{
          user_id: userId,
          name: userData.name,
          email: userData.email,
          roles: userData.roles || ['Viewer'],
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        // If it's a unique constraint error, try to get existing profile
        if (error.code === '23505') {
          return await this.getUserProfile(userId);
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Create user profile error:', error);
      // Return mock profile for demo
      return {
        id: `profile-${Date.now()}`,
        user_id: userId,
        ...userData,
        created_at: new Date().toISOString()
      };
    }
  }

  async getUserProfile(userId) {
    try {
      if (this.usesMockData) {
        return {
          id: `profile-${userId}`,
          user_id: userId,
          name: 'Demo User',
          email: 'demo@example.com',
          roles: ['Admin']
        };
      }

      const { data, error } = await supabase
        .from('user_profiles_portal2025')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get user profile error:', error);
      // Return mock profile for demo
      return {
        id: `profile-${userId}`,
        user_id: userId,
        name: 'Demo User',
        email: 'demo@example.com',
        roles: ['Admin']
      };
    }
  }

  async updateUserProfile(profileId, updates) {
    try {
      if (this.usesMockData) {
        return {
          id: profileId,
          ...updates,
          updated_at: new Date().toISOString()
        };
      }

      const { data, error } = await supabase
        .from('user_profiles_portal2025')
        .update({
          name: updates.name,
          email: updates.email,
          roles: updates.roles,
          updated_at: new Date().toISOString()
        })
        .eq('id', profileId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update user profile error:', error);
      throw error;
    }
  }

  async deleteUser(profileId) {
    try {
      if (this.usesMockData) {
        return true;
      }

      // First get the profile to find the auth user ID
      const { data: profile } = await supabase
        .from('user_profiles_portal2025')
        .select('user_id')
        .eq('id', profileId)
        .single();

      if (profile?.user_id) {
        // Try to delete the auth user (admin operation)
        try {
          const { error: authError } = await supabase.auth.admin.deleteUser(profile.user_id);
          if (authError) {
            console.warn('Could not delete auth user (admin required):', authError);
          }
        } catch (adminError) {
          console.warn('Admin delete not available:', adminError);
        }
      }

      // Delete the profile
      const { error } = await supabase
        .from('user_profiles_portal2025')
        .delete()
        .eq('id', profileId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  // User permissions methods
  async getUserPermissions(userId) {
    try {
      if (this.usesMockData) {
        const mockUser = this.getMockUsers().find(u => u.user_id === userId || u.id === userId);
        return {
          companies: mockUser?.companyAccess || [],
          assets: mockUser?.assetPermissions || {}
        };
      }

      // Get company access
      const { data: companyAccess } = await supabase
        .from('user_companies_portal2025')
        .select('company_id')
        .eq('user_id', userId);

      // Get asset permissions
      const { data: assetPermissions } = await supabase
        .from('user_asset_permissions_portal2025')
        .select('asset_id, granted')
        .eq('user_id', userId);

      const companies = companyAccess ? companyAccess.map(c => c.company_id) : [];
      const assets = {};
      if (assetPermissions) {
        assetPermissions.forEach(permission => {
          assets[permission.asset_id] = permission.granted;
        });
      }

      return { companies, assets };
    } catch (error) {
      console.error('Get user permissions error:', error);
      return { companies: [], assets: {} };
    }
  }

  async updateUserPermissions(userId, permissions) {
    try {
      if (this.usesMockData) {
        return true; // Simulate success in mock mode
      }

      // Update company access
      // First, remove all existing company access
      await supabase
        .from('user_companies_portal2025')
        .delete()
        .eq('user_id', userId);

      // Then add new company access
      if (permissions.companies && permissions.companies.length > 0) {
        const companyInserts = permissions.companies.map(companyId => ({
          user_id: userId,
          company_id: companyId,
          created_at: new Date().toISOString()
        }));

        await supabase
          .from('user_companies_portal2025')
          .insert(companyInserts);
      }

      // Update asset permissions
      // First, remove all existing asset permissions
      await supabase
        .from('user_asset_permissions_portal2025')
        .delete()
        .eq('user_id', userId);

      // Then add new asset permissions
      if (permissions.assets && Object.keys(permissions.assets).length > 0) {
        const assetInserts = Object.entries(permissions.assets)
          .filter(([assetId, granted]) => granted) // Only insert granted permissions
          .map(([assetId, granted]) => ({
            user_id: userId,
            asset_id: assetId,
            granted: granted,
            created_at: new Date().toISOString()
          }));

        if (assetInserts.length > 0) {
          await supabase
            .from('user_asset_permissions_portal2025')
            .insert(assetInserts);
        }
      }

      return true;
    } catch (error) {
      console.error('Update user permissions error:', error);
      throw error;
    }
  }

  // Grant user access to company
  async grantUserCompanyAccess(userId, companyId) {
    try {
      if (this.usesMockData) {
        return;
      }

      const { data, error } = await supabase
        .from('user_companies_portal2025')
        .insert([{
          user_id: userId,
          company_id: companyId,
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Grant user company access error:', error);
      throw error;
    }
  }
}

export const supabaseService = new SupabaseService();